/**
 * CSV Export Service for habitué Merchant Analytics
 * Exports analytics data to CSV format for download
 */

/**
 * Convert array of objects to CSV string
 * @param {Array} data - Array of objects to convert
 * @param {Array} columns - Column definitions [{key, label}]
 * @returns {string} CSV formatted string
 */
export const arrayToCSV = (data, columns) => {
  if (!data || data.length === 0) return '';
  
  // Header row
  const header = columns.map(col => `"${col.label}"`).join(',');
  
  // Data rows
  const rows = data.map(row => {
    return columns.map(col => {
      let value = row[col.key];
      
      // Handle different types
      if (value === null || value === undefined) {
        value = '';
      } else if (typeof value === 'number') {
        // Format numbers
        if (col.format === 'currency') {
          value = value.toFixed(2);
        } else if (col.format === 'percent') {
          value = value.toFixed(1);
        }
      } else if (value instanceof Date) {
        value = value.toISOString().split('T')[0];
      }
      
      // Escape quotes and wrap in quotes if contains comma
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(',');
  });
  
  return [header, ...rows].join('\n');
};

/**
 * Download CSV file
 * @param {string} csvContent - CSV formatted string
 * @param {string} filename - Name of the file to download
 */
export const downloadCSV = (csvContent, filename) => {
  // Add BOM for Excel UTF-8 compatibility
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Cleanup
  URL.revokeObjectURL(url);
};

/**
 * Export revenue data to CSV
 */
export const exportRevenueData = (data, period, language = 'en') => {
  const columns = [
    { key: 'name', label: language === 'fr' ? 'Période' : 'Period' },
    { key: 'revenue', label: language === 'fr' ? 'Chiffre d\'affaires (£)' : 'Revenue (£)', format: 'currency' },
    { key: 'transactions', label: language === 'fr' ? 'Transactions' : 'Transactions' },
    { key: 'giftCards', label: language === 'fr' ? 'Cartes cadeaux' : 'Gift Cards' },
  ];
  
  const csv = arrayToCSV(data, columns);
  const date = new Date().toISOString().split('T')[0];
  downloadCSV(csv, `habitue-revenue-${period}-${date}.csv`);
};

/**
 * Export hourly traffic data to CSV
 */
export const exportHourlyData = (data, language = 'en') => {
  const columns = [
    { key: 'hour', label: language === 'fr' ? 'Heure' : 'Hour' },
    { key: 'traffic', label: language === 'fr' ? 'Visiteurs' : 'Visitors' },
  ];
  
  const csv = arrayToCSV(data, columns);
  const date = new Date().toISOString().split('T')[0];
  downloadCSV(csv, `habitue-hourly-traffic-${date}.csv`);
};

/**
 * Export category breakdown to CSV
 */
export const exportCategoryData = (data, language = 'en') => {
  const columns = [
    { key: 'name', label: language === 'fr' ? 'Catégorie' : 'Category' },
    { key: 'value', label: language === 'fr' ? 'Part (%)' : 'Share (%)', format: 'percent' },
  ];
  
  const csv = arrayToCSV(data, columns);
  const date = new Date().toISOString().split('T')[0];
  downloadCSV(csv, `habitue-categories-${date}.csv`);
};

/**
 * Export full analytics summary to CSV
 */
export const exportFullAnalytics = (analyticsData, period, language = 'en') => {
  const { summary, revenueData, hourlyData, categoryData, loyaltyData } = analyticsData;
  
  // Build comprehensive report
  let csvContent = '';
  
  // Section 1: Summary
  const summarySection = language === 'fr' ? 'RÉSUMÉ' : 'SUMMARY';
  csvContent += `${summarySection}\n`;
  csvContent += `${language === 'fr' ? 'Métrique' : 'Metric'},${language === 'fr' ? 'Valeur' : 'Value'},${language === 'fr' ? 'Variation (%)' : 'Change (%)'}\n`;
  csvContent += `${language === 'fr' ? 'Chiffre d\'affaires total' : 'Total Revenue'},£${summary.totalRevenue.toFixed(2)},${summary.revenueChange}%\n`;
  csvContent += `${language === 'fr' ? 'Transactions' : 'Transactions'},${summary.totalTransactions},${summary.transactionChange}%\n`;
  csvContent += `${language === 'fr' ? 'Panier moyen' : 'Average Ticket'},£${summary.avgTicket.toFixed(2)},${summary.ticketChange}%\n`;
  csvContent += `${language === 'fr' ? 'Nouveaux clients' : 'New Customers'},${summary.newCustomers},${summary.customerChange}%\n`;
  csvContent += `${language === 'fr' ? 'Taux de fidélité' : 'Repeat Rate'},${summary.repeatRate}%,\n`;
  csvContent += `${language === 'fr' ? 'Points distribués' : 'Points Issued'},${summary.pointsIssued},\n`;
  csvContent += `${language === 'fr' ? 'Cartes cadeaux vendues' : 'Gift Cards Sold'},${summary.giftCardsSold},\n`;
  csvContent += `${language === 'fr' ? 'Cartes cadeaux utilisées' : 'Gift Cards Redeemed'},${summary.giftCardsRedeemed},\n`;
  csvContent += '\n';
  
  // Section 2: Revenue Over Time
  const revenueSection = language === 'fr' ? 'ÉVOLUTION DU CA' : 'REVENUE OVER TIME';
  csvContent += `${revenueSection}\n`;
  csvContent += `${language === 'fr' ? 'Période' : 'Period'},${language === 'fr' ? 'CA (£)' : 'Revenue (£)'},${language === 'fr' ? 'Transactions' : 'Transactions'},${language === 'fr' ? 'Cartes cadeaux' : 'Gift Cards'}\n`;
  revenueData.forEach(row => {
    csvContent += `${row.name},${row.revenue.toFixed(2)},${row.transactions},${row.giftCards}\n`;
  });
  csvContent += '\n';
  
  // Section 3: Customer Loyalty
  const loyaltySection = language === 'fr' ? 'FIDÉLITÉ CLIENTS' : 'CUSTOMER LOYALTY';
  csvContent += `${loyaltySection}\n`;
  csvContent += `${language === 'fr' ? 'Type' : 'Type'},${language === 'fr' ? 'Part (%)' : 'Share (%)'}\n`;
  loyaltyData.forEach(row => {
    csvContent += `${row.name},${row.value}\n`;
  });
  csvContent += '\n';
  
  // Section 4: Hourly Traffic
  const trafficSection = language === 'fr' ? 'TRAFIC PAR HEURE' : 'HOURLY TRAFFIC';
  csvContent += `${trafficSection}\n`;
  csvContent += `${language === 'fr' ? 'Heure' : 'Hour'},${language === 'fr' ? 'Visiteurs' : 'Visitors'}\n`;
  hourlyData.forEach(row => {
    csvContent += `${row.hour},${row.traffic}\n`;
  });
  csvContent += '\n';
  
  // Section 5: Categories
  const categorySection = language === 'fr' ? 'TOP CATÉGORIES' : 'TOP CATEGORIES';
  csvContent += `${categorySection}\n`;
  csvContent += `${language === 'fr' ? 'Catégorie' : 'Category'},${language === 'fr' ? 'Part (%)' : 'Share (%)'}\n`;
  categoryData.forEach(row => {
    csvContent += `${row.name},${row.value}\n`;
  });
  
  // Download
  const date = new Date().toISOString().split('T')[0];
  const periodLabel = period === 'week' ? '7days' : period === 'month' ? '30days' : '12months';
  downloadCSV(csvContent, `habitue-analytics-${periodLabel}-${date}.csv`);
};

export default {
  arrayToCSV,
  downloadCSV,
  exportRevenueData,
  exportHourlyData,
  exportCategoryData,
  exportFullAnalytics,
};
